import BigNumber from 'bignumber.js';
import { useMemo } from 'react';

import { useGetVTokenCash } from 'clients/api';
import { NULL_ADDRESS } from 'constants/address';
import { HEALTH_FACTOR_SAFE_MAX_THRESHOLD } from 'constants/healthFactor';
import type { Asset, Pool } from 'types';
import { convertMantissaToTokens } from 'utilities';

interface UseBundledBorrowLimitsInput {
  borrowTargetAsset?: Asset;
  bundleBorrowAmountTokens: string;
  bundleBorrowEnabled: boolean;
  bundleBorrowLegActive: boolean;
  canBundleOpenPosition: boolean;
  pool: Pool;
  setBundleBorrowAmountTokens: React.Dispatch<React.SetStateAction<string>>;
  supplyOnlySimulatedPool?: Pool;
}

export const useBundledBorrowLimits = ({
  borrowTargetAsset,
  bundleBorrowAmountTokens,
  bundleBorrowEnabled,
  bundleBorrowLegActive,
  canBundleOpenPosition,
  pool,
  setBundleBorrowAmountTokens,
  supplyOnlySimulatedPool,
}: UseBundledBorrowLimitsInput) => {
  const { data: borrowTargetCashData } = useGetVTokenCash(
    { vTokenAddress: borrowTargetAsset?.vToken.address ?? NULL_ADDRESS },
    { enabled: !!borrowTargetAsset && canBundleOpenPosition && bundleBorrowEnabled },
  );
  const borrowTargetAvailableTokens =
    borrowTargetCashData && borrowTargetAsset
      ? convertMantissaToTokens({
          value: borrowTargetCashData.cashMantissa,
          token: borrowTargetAsset.vToken.underlyingToken,
        })
      : undefined;

  const bundleBorrowExceedsCash =
    canBundleOpenPosition &&
    bundleBorrowEnabled &&
    !!borrowTargetAvailableTokens &&
    Number(bundleBorrowAmountTokens) > 0 &&
    new BigNumber(bundleBorrowAmountTokens).isGreaterThan(borrowTargetAvailableTokens);
  const bundleBorrowAmountMissing =
    canBundleOpenPosition && bundleBorrowEnabled && !(Number(bundleBorrowAmountTokens) > 0);
  const [maxBundledBorrowTokens, safeMaxBundledBorrowTokens] = useMemo<
    [BigNumber, BigNumber]
  >(() => {
    const zero: [BigNumber, BigNumber] = [new BigNumber(0), new BigNumber(0)];
    if (!bundleBorrowEnabled || !canBundleOpenPosition || !borrowTargetAsset) {
      return zero;
    }
    const refPool = supplyOnlySimulatedPool ?? pool;
    if (
      !refPool.userBorrowBalanceCents ||
      !refPool.userBorrowLimitCents ||
      !refPool.userLiquidationThresholdCents ||
      refPool.userBorrowBalanceCents.isGreaterThanOrEqualTo(refPool.userBorrowLimitCents)
    ) {
      return zero;
    }
    const targetPriceCents = borrowTargetAsset.tokenPriceCents;
    if (targetPriceCents.isLessThanOrEqualTo(0)) return zero;

    const liquidityFloorTokens =
      borrowTargetAvailableTokens ??
      new BigNumber(borrowTargetAsset.liquidityCents).dividedBy(targetPriceCents);
    const borrowLimitFloorTokens = refPool.userBorrowLimitCents
      .minus(refPool.userBorrowBalanceCents)
      .dividedBy(targetPriceCents);
    let safeHfFloorTokens = refPool.userLiquidationThresholdCents
      .div(HEALTH_FACTOR_SAFE_MAX_THRESHOLD)
      .minus(refPool.userBorrowBalanceCents)
      .dividedBy(targetPriceCents);
    if (safeHfFloorTokens.isLessThan(0)) safeHfFloorTokens = new BigNumber(0);
    const borrowCapFloorTokens = borrowTargetAsset.borrowCapTokens.minus(
      borrowTargetAsset.borrowBalanceTokens,
    );
    const decimals = borrowTargetAsset.vToken.underlyingToken.decimals;
    const hardMax = BigNumber.min(
      liquidityFloorTokens,
      borrowLimitFloorTokens,
      borrowCapFloorTokens,
    ).dp(decimals, BigNumber.ROUND_DOWN);
    const safeMax = BigNumber.min(hardMax, safeHfFloorTokens).dp(decimals, BigNumber.ROUND_DOWN);
    return [hardMax, safeMax];
  }, [
    bundleBorrowEnabled,
    canBundleOpenPosition,
    borrowTargetAsset,
    supplyOnlySimulatedPool,
    pool,
    borrowTargetAvailableTokens,
  ]);

  const bundleBorrowExceedsLimit =
    bundleBorrowLegActive &&
    new BigNumber(bundleBorrowAmountTokens).isGreaterThan(maxBundledBorrowTokens);
  const bundleBorrowExceedsSafeMax =
    bundleBorrowLegActive &&
    new BigNumber(bundleBorrowAmountTokens).isGreaterThan(safeMaxBundledBorrowTokens);

  return {
    borrowTargetAvailableTokens,
    bundleBorrowAmountMissing,
    bundleBorrowExceedsCash,
    bundleBorrowExceedsLimit,
    bundleBorrowExceedsSafeMax,
    bundleBorrowLegActive,
    handleBundleBorrowSafeMaxClick: () => {
      setBundleBorrowAmountTokens(safeMaxBundledBorrowTokens.toFixed());
    },
    maxBundledBorrowTokens,
    safeMaxBundledBorrowTokens,
  };
};
